import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { patientsApi, doctorsApi, companiesApi } from "@/lib/api";
import { toast } from "sonner";
import { Users, Plus, Pencil, Trash2, Search } from "lucide-react";

export default function Patients() {
  const [searchParams] = useSearchParams();
  const refreshKey = searchParams.get('_r');
  
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPatient, setEditingPatient] = useState(null);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    birth_date: "",
    gender: "",
    phone: "",
    email: "",
    address: "",
    doctor_id: "",
    company_id: "",
    expediente: "", // Preservar expediente al editar
  });

  const getToday = () => {
    const now = new Date();
    const offset = now.getTimezoneOffset();
    const local = new Date(now.getTime() - offset * 60000);
    return local.toISOString().split('T')[0];
  };

  // Primera carga - solo pacientes del día
  useEffect(() => {
    fetchData(true);
  }, []);

  // Refrescos posteriores - sin indicador
  useEffect(() => {
    if (refreshKey) {
      fetchData(false);
    }
  }, [refreshKey]);

  const fetchData = async (showLoading = false, search = "") => {
    try {
      const todayDate = search ? undefined : getToday();
      const [patientsRes, doctorsRes, companiesRes] = await Promise.all([
        patientsApi.getAll(search || undefined, todayDate),
        doctorsApi.getAll(),
        companiesApi.getAll(),
      ]);
      setPatients(patientsRes.data);
      setDoctors(doctorsRes.data);
      setCompanies(companiesRes.data);
    } catch (error) {
      toast.error("Error al cargar datos");
    } finally {
      if (showLoading) setLoading(false);
    }
  };

  const handleOpenDialog = (patient = null) => {
    if (patient) {
      setEditingPatient(patient);
      setFormData({
        first_name: patient.first_name,
        last_name: patient.last_name,
        birth_date: patient.birth_date,
        gender: patient.gender,
        phone: patient.phone || "",
        email: patient.email || "",
        address: patient.address || "",
        doctor_id: patient.doctor_id || "",
        company_id: patient.company_id || "",
        expediente: patient.expediente || "", // Preservar expediente existente
      });
    } else {
      setEditingPatient(null);
      setFormData({
        first_name: "",
        last_name: "",
        birth_date: "",
        gender: "",
        phone: "",
        email: "",
        address: "",
        doctor_id: "",
        company_id: "",
        expediente: "", // Nuevo paciente - se generará en backend
      });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSend = { ...formData };
      if (!dataToSend.doctor_id) delete dataToSend.doctor_id;
      if (!dataToSend.company_id) delete dataToSend.company_id;
      
      // Para nuevos pacientes, no enviar expediente vacío (se generará en backend)
      if (!editingPatient && !dataToSend.expediente) {
        delete dataToSend.expediente;
      }

      if (editingPatient) {
        await patientsApi.update(editingPatient.id, dataToSend);
        toast.success("Paciente actualizado");
      } else {
        await patientsApi.create(dataToSend);
        toast.success("Paciente creado");
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Error al guardar paciente");
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("¿Está seguro de eliminar este paciente?")) return;
    try {
      await patientsApi.delete(id);
      toast.success("Paciente eliminado");
      fetchData();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    if (searchTimeout) clearTimeout(searchTimeout);
    setSearchTimeout(setTimeout(() => {
      fetchData(false, value);
    }, 400));
  };

  const filteredPatients = patients;

  return (
    <div className="space-y-6" data-testid="patients-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold font-['Manrope'] tracking-tight">
            Pacientes
          </h1>
          <p className="text-muted-foreground">
            Administre el registro de pacientes
          </p>
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="add-patient-btn">
          <Plus className="h-4 w-4 mr-2" />
          Nuevo Paciente
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Users className="h-5 w-5" />
              Registro de Pacientes
            </CardTitle>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-10 w-64"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Cargando...</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Fecha Nac.</TableHead>
                  <TableHead>Género</TableHead>
                  <TableHead>Teléfono</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPatients.map((patient) => (
                  <TableRow key={patient.id}>
                    <TableCell className="font-medium">
                      {patient.first_name} {patient.last_name}
                    </TableCell>
                    <TableCell>{patient.birth_date}</TableCell>
                    <TableCell>
                      {patient.gender === "male"
                        ? "Masculino"
                        : patient.gender === "female"
                        ? "Femenino"
                        : "Otro"}
                    </TableCell>
                    <TableCell>{patient.phone || "-"}</TableCell>
                    <TableCell>{patient.email || "-"}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleOpenDialog(patient)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(patient.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingPatient ? "Editar Paciente" : "Nuevo Paciente"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Nombre *</Label>
                <Input
                  value={formData.first_name}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, first_name: e.target.value }))
                  }
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Apellidos *</Label>
                <Input
                  value={formData.last_name}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, last_name: e.target.value }))
                  }
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Fecha de Nacimiento *</Label>
                <Input
                  type="date"
                  value={formData.birth_date}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, birth_date: e.target.value }))
                  }
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Género *</Label>
                <Select
                  value={formData.gender}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, gender: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Masculino</SelectItem>
                    <SelectItem value="female">Femenino</SelectItem>
                    <SelectItem value="other">Otro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Teléfono</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, phone: e.target.value }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, email: e.target.value }))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label>Médico</Label>
                <Select
                  value={formData.doctor_id}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, doctor_id: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    {doctors.map((doctor) => (
                      <SelectItem key={doctor.id} value={doctor.id}>
                        {doctor.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Compañía</Label>
                <Select
                  value={formData.company_id}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, company_id: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar" />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.map((company) => (
                      <SelectItem key={company.id} value={company.id}>
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Dirección</Label>
                <Textarea
                  value={formData.address}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, address: e.target.value }))
                  }
                  rows={2}
                />
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button type="submit" data-testid="save-patient-btn">
                {editingPatient ? "Actualizar" : "Crear"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
